import React, { useEffect, useState } from "react";
import BaseLayout from "../../../components/Layout";
import {
  Store,
  Server,
  Cpu,
  Activity,
  ToggleLeft,
  Gauge,
  ChevronRight,
  ChevronLeft,
  Save,
  Trash2,
  Plus,
  SkipForward,
  RotateCcw,
  Link2,
  Unlink,
  Building2,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  salvarInstalacaoCompleta,
  resetStatus,
} from "../../../features/tecnico/tecnicoConfig/slices";
import { RootState, AppDispatch } from "../../../store";
import * as versionData from "../../../version.json";

import "./styles.scss";
import { getUser } from "../../../helpers/user";
import { useAppSelector } from "../../../hooks/useAppSelector";
import RequiredLabel from "../../../components/RequiredLabel/RequiredLabel";

const isDev =
  typeof process !== "undefined" && process.env.NODE_ENV === "development";

const steps = [
  {
    id: "regional",
    title: "Regional",
    icon: <Building2 size={20} />,
    optional: true,
  },
  {
    id: "loja",
    title: "Dados da Loja",
    icon: <Store size={20} />,
    optional: true,
  },
  {
    id: "dispcentral",
    title: "Disp. Central",
    icon: <Server size={20} />,
    optional: true,
  },
  {
    id: "dispmodulo",
    title: "Disp. Módulo",
    icon: <Cpu size={20} />,
    optional: true,
  },
  {
    id: "monanalogico",
    title: "Mon. Analógico",
    icon: <Activity size={20} />,
    optional: true,
    devOnly: true,
  },
  {
    id: "mondigital",
    title: "Mon. Digital",
    icon: <ToggleLeft size={20} />,
    optional: true,
    devOnly: true,
  },
  {
    id: "monmedidor",
    title: "Mon. Medidor",
    icon: <Gauge size={20} />,
    optional: true,
    devOnly: true,
  },
];

const lastStepIndex = steps.reduce(
  (acc, step, index) => (isDev || !step.devOnly ? index : acc),
  0,
);

const isStepVisible = (index: number) => {
  const step = steps[index];
  return step ? isDev || !step.devOnly : false;
};

const OPTIONAL_STEP_SECTION_MAP: Record<number, string> = {
  0: "regional",
  1: "loja",
  2: "dispcentral",
  3: "dispmodulo",
  4: "monanalogico",
  5: "mondigital",
  6: "monmedidor",
};

const AVULSO_FIELDS: Record<number, { key: string; label: string }[]> = {
  2: [{ key: "idLoja", label: "ID da Loja" }],
  3: [
    { key: "idLoja", label: "ID da Loja" },
    { key: "idCentral", label: "ID da Central" },
  ],
  4: [
    { key: "idLoja", label: "ID da Loja" },
    { key: "idCentral", label: "ID da Central" },
    { key: "idModulo", label: "ID do Módulo" },
  ],
  5: [
    { key: "idLoja", label: "ID da Loja" },
    { key: "idCentral", label: "ID da Central" },
    { key: "idModulo", label: "ID do Módulo" },
  ],
  6: [
    { key: "idLoja", label: "ID da Loja" },
    { key: "idCentral", label: "ID da Central" },
    { key: "idModulo", label: "ID do Módulo" },
  ],
};

const eqpAnalogicos = [
  { id: 2, nome: "Reservatório RTI" },
  { id: 6, nome: "VGA" },
  { id: 20, nome: "Hidrantes" },
  { id: 22, nome: "Tensão Elétrica" },
  { id: 40, nome: "Cavalete Bomba Jockey" },
];

const eqpDigitais = [
  { id: 1, nome: "Painel Elétrico" },
  { id: 7, nome: "Central de Alarmes" },
  { id: 18, nome: "Painel Elétrico Btn. Emergência" },
  { id: 24, nome: "Porta Câmara Fria" },
  { id: 33, nome: "Relê Térmico Bomba JOCKEY" },
  { id: 35, nome: "Chave Aut/Man Bomba JOCKEY" },
  { id: 38, nome: "Chave Aut/Man Bomba Principal" },
  { id: 39, nome: "Relê Térmico Bomba Principal" },
  { id: 41, nome: "Relê Falta de Fase Bomba Jockey" },
  { id: 42, nome: "Relê Falta de Fase Bomba Principal" },
];

const eqpMedidores = [
  { id: 30, nome: "Medidor de Energia" },
  { id: 32, nome: "Medidor de Água" },
];

const defaultRegional = {
  idGerencia: 1,
  idDivisao: 1,
  nome: "",
  descricao: "",
  ativo: 1,
};

const defaultCentral = {
  modelo: "CA8D4",
  versao: "4.0",
  taxaEnvio: 20,
  apPassword: "ikone@MonitoraAP",
  patrimonio: "",
  endPoint: "http://monitorasompoapi.somee.com/api/Dispositivo",
  loraBand: 923000000,
  alertaAtivo: 0,
  envioEmailAtivo: 0,
  envioWhatsAtivo: 0,
  envioTelegramAtivo: 0,
  apiPostInerval: 25000,
  loraSendInterval: 2000,
  wifiTryCount: 30,
  maxRemoteModule: 2,
  apiErrorLimit: 10,
  analogResolution: 11,
  analogConst: 21,
  timeMsgEscaladaNV2: "0",
  timeMsgEscaladaNV3: "0",
  toleranciaOffline: "30",
};

const defaultModulo = {
  adrModulo: 0,
  modelo: "A8D4",
  versao: "4.0",
  toleranciaOffline: 30,
  centralIndex: 0,
  idModulo: "",
};

const defaultAnalogico = {
  idEntrada: 0,
  idGrupoMonitor: 1,
  idEquipamento: 40,
  modeloModulo: "A8D4",
  unidadeMedida: "PSI",
  comentarios: "-",
  escalaInf: 0,
  escalaSup: 100,
  limiteInf: 10,
  calibracaoEscala: 1,
  limiteSup: 100,
  toleranciaValor: 15,
  toleranciaTime: 6,
  msgNormalCustom: "pressurizado",
  msgFalhaAcimaCustom: "com pressão acima do limite",
  msgFalhaAbaixoCustom: "com pressão abaixo do limite",
  procedimento: "",
  descricao: "",
  idLoja: "", // Adicionado para suportar modo avulso
  idCentral: "", // Adicionado para suportar modo avulso
  idModulo: "",
};

const defaultDigital = {
  idEntrada: 0,
  idGrupoMonitor: 1,
  idEquipamento: 1,
  modeloModulo: "A8D4",
  comentarios: "-",
  valorPadraoNormal: 1,
  toleranciaTime: 0,
  msgNormalCustom: "operando normalmente",
  msgFalhaCustom: "em falha ou desenergizado",
};

const defaultMedidor = {
  idEntrada: 0,
  idGrupoMonitor: 1,
  idEquipamento: 30,
  modeloModulo: "A8D4",
  comentarios: "-",
  diaMedicao: 0,
  limiteInf: 5,
  limiteSup: 75,
  unidadeMedida: "BAR",
  moduloIndex: 0,
};

const handleNumberInput = (value: string) => {
  if (value === "") return "";
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? "" : parsed;
};

const getModeloModuloFromCentral = (centralModelo: string) =>
  centralModelo.replace(/^C/, "");

const formatCurrency = (value: string | number) => {
  if (!value) return "";
  const onlyDigits = String(value).replace(/\D/g, "");
  const numberValue = Number(onlyDigits) / 100;
  return numberValue.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

interface AvulsoBannerProps {
  stepIndex: number;
  avulsoIds: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onDisable: () => void;
}

const AvulsoBanner: React.FC<AvulsoBannerProps> = ({
  stepIndex,
  avulsoIds,
  onChange,
  onDisable,
}) => {
  const fields = AVULSO_FIELDS[stepIndex];
  if (!fields) return null;

  return (
    <div className="avulso-banner">
      <div className="avulso-banner-header">
        <div className="avulso-banner-title">
          <Unlink size={16} />
          <span>
            <strong>Modo Avulso</strong> — Informe os IDs existentes para
            vincular este cadastro sem percorrer os passos anteriores.
          </span>
        </div>
        <button
          className="btn-disable-avulso"
          onClick={onDisable}
          title="Voltar ao fluxo completo"
        >
          <Link2 size={14} /> Usar fluxo completo
        </button>
      </div>
      <div className="avulso-fields">
        {fields.map((f) => (
          <div key={f.key} className="avulso-field">
            <label>
              {f.label} <span className="required">*</span>
            </label>
            <input
              type="number"
              placeholder="Ex: 42"
              value={avulsoIds[f.key] ?? ""}
              onChange={(e) => onChange(f.key, e.target.value)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const TecnicoProcessoCriacoesPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [skippedSteps, setSkippedSteps] = useState<Set<number>>(new Set());

  const [avulsoConfig, setAvulsoConfig] = useState<
    Record<number, { ativo: boolean; ids: Record<string, string> }>
  >({});

  const user = getUser();
  const { major, minor, patch, build } = versionData || {
    major: 1,
    minor: 0,
    patch: 0,
    build: 0,
  };
  const versaoFormatada = `${major}.${minor}.${patch}.${build}`;

  const dispatch = useDispatch<AppDispatch>();
  const { loading, error, success } = useSelector(
    (state: RootState) => state.sliceTecnico,
  );

  useEffect(() => {
    if (success) {
      alert("Instalação completa realizada com sucesso!");
      dispatch(resetStatus());
      setActiveStep(0);
      setSkippedSteps(new Set());
      setAvulsoConfig({});
    }
    if (error) {
      alert("Erro ao salvar: " + error);
      dispatch(resetStatus());
    }
  }, [success, error, dispatch]);

  const [formData, setFormData] = useState({
    regional: { ...defaultRegional },
    loja: {
      numero: "",
      nome: "",
      idSeguradora: 0,
      idRegional: 0,
      idCoordenacao: 0,
      importanciaSegurada: 0,
      arquivoPlanta: "",
      endereco: "",
      uf: "",
      latitude: "",
      longitude: "",
      cnpj: "",
      vigencia: "",
      ativo: 1,
    },
    dispcentral: [{ ...defaultCentral }],
    dispmodulo: [{ ...defaultModulo }],
    monanalogico: [{ ...defaultAnalogico, moduloIndex: 0 }],
    mondigital: [{ ...defaultDigital, moduloIndex: 0 }],
    monmedidor: [{ ...defaultMedidor, moduloIndex: 0 }],
  });

  const handleNext = () =>
    setActiveStep((prev) => {
      let next = prev + 1;
      while (next <= lastStepIndex && !isStepVisible(next)) next++;
      return next > lastStepIndex ? prev : next;
    });

  const handlePrev = () =>
    setActiveStep((prev) => {
      let next = prev - 1;
      while (next >= 0 && !isStepVisible(next)) next--;
      return next < 0 ? prev : next;
    });

  const handleSkip = () => {
    setSkippedSteps((prev) => new Set(prev).add(activeStep));
    handleNext();
  };

  const handleUnskip = (index: number) => {
    setSkippedSteps((prev) => {
      const next = new Set(prev);
      next.delete(index);
      return next;
    });
  };

  const enableAvulso = (stepIndex: number) => {
    handleUnskip(stepIndex);
    setAvulsoConfig((prev) => ({
      ...prev,
      [stepIndex]: { ativo: true, ids: prev[stepIndex]?.ids ?? {} },
    }));
  };

  const disableAvulso = (stepIndex: number) => {
    setAvulsoConfig((prev) => {
      const next = { ...prev };
      delete next[stepIndex];
      return next;
    });
  };

  const setAvulsoId = (stepIndex: number, key: string, value: string) => {
    setAvulsoConfig((prev) => ({
      ...prev,
      [stepIndex]: {
        ativo: true,
        ids: { ...(prev[stepIndex]?.ids ?? {}), [key]: value },
      },
    }));
  };

  const isAvulso = (stepIndex: number) =>
    avulsoConfig[stepIndex]?.ativo === true;

  const handleObjectChange = (section: string, field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [section]: { ...(prev as any)[section], [field]: value },
    }));
  };

  const handleArrayChange = (
    section: string,
    index: number,
    field: string,
    value: any,
  ) => {
    setFormData((prev) => {
      const newArray = [...(prev as any)[section]];
      newArray[index] = { ...newArray[index], [field]: value };
      return { ...prev, [section]: newArray };
    });
  };

  const addMonitor = (section: string, defaultData: object) => {
    setFormData((prev) => ({
      ...prev,
      [section]: [...(prev as any)[section], { ...defaultData }],
    }));
  };

  const removeMonitor = (section: string, index: number) => {
    setFormData((prev) => {
      const newArray = [...(prev as any)[section]];
      newArray.splice(index, 1);
      return { ...prev, [section]: newArray };
    });
  };

  const onSubmit = () => {
    const cleanedFormData: any = { ...formData };

    skippedSteps.forEach((stepIndex) => {
      const section = OPTIONAL_STEP_SECTION_MAP[stepIndex];
      if (section) cleanedFormData[section] = [];
    });

    Object.entries(avulsoConfig).forEach(([stepIdxStr, config]) => {
      if (!config.ativo) return;
      const section = OPTIONAL_STEP_SECTION_MAP[Number(stepIdxStr)];
      if (!section) return;
      cleanedFormData[section] = (cleanedFormData[section] as any[]).map(
        (item: any) => ({
          ...item,
          ...Object.fromEntries(
            Object.entries(config.ids).map(([k, v]) => [k, Number(v)]),
          ),
        }),
      );
    });

    const payload = {
      formData: cleanedFormData,
      idCliente: user?.usuario?.idCliente || 1,
      avulsoConfig,
    };

    console.log("Enviando JSON:", payload);
    dispatch(salvarInstalacaoCompleta(payload));
  };

  const isCurrentStepOptional = steps[activeStep].optional;
  const isCurrentStepSkipped = skippedSteps.has(activeStep);
  const isCurrentStepAvulso = isAvulso(activeStep);
  const currentStepHasAvulsoFields = AVULSO_FIELDS[activeStep] !== undefined;

  const canGoNext = () => {
    const section = OPTIONAL_STEP_SECTION_MAP[activeStep];
    if (!section) return true;

    const hasData =
      section === "regional"
        ? formData.regional.nome !== ""
        : section === "loja"
          ? formData.loja.nome !== ""
          : (formData as any)[section]?.length > 0;

    if (activeStep === 2 && skippedSteps.has(1)) {
      return formData.dispcentral.every((c) => Number((c as any).idLoja) > 0);
    }

    return hasData || skippedSteps.has(activeStep) || isAvulso(activeStep);
  };

  const clienteSelected =
    useAppSelector((state) => state.getRegionais.regionalSelected) || "";

  const todasRegionais = useAppSelector(
    (state) => state.getRegionais.regionais || [],
  );

  const clienteObjetoSelected = useAppSelector((state) => {
    const lista = state.getRegionais.regionais || [];
    return lista.find((r: any) => r.id === Number(clienteSelected));
  }) as any;

  console.log("Regional selecionada: ", clienteObjetoSelected);

  useEffect(() => {
    if (clienteSelected && skippedSteps.has(0)) {
      setFormData((prev) => ({
        ...prev,
        loja: {
          ...prev.loja,
          idRegional: Number(clienteSelected), // Força a tipagem como número
        },
      }));
    }
  }, [clienteSelected, skippedSteps]);

  const isFormVazio = () => {
    const temRegional = formData.regional.nome.trim() !== "";
    const temLoja = formData.loja.nome.trim() !== "";
    const temCentral = formData.dispcentral.length > 0;
    const temModulo = formData.dispmodulo.length > 0;
    const temAnalogico = formData.monanalogico.length > 0;
    const temDigital = formData.mondigital.length > 0;
    const temMedidor = formData.monmedidor.length > 0;

    const temAlgoParaSalvar =
      (!skippedSteps.has(0) && temRegional) ||
      (!skippedSteps.has(1) && temLoja) ||
      (!skippedSteps.has(2) && temCentral) ||
      (!skippedSteps.has(3) && temModulo) ||
      (!skippedSteps.has(4) && temAnalogico) ||
      (!skippedSteps.has(5) && temDigital) ||
      (!skippedSteps.has(6) && temMedidor) ||
      Object.values(avulsoConfig).some((c) => c.ativo);

    return !temAlgoParaSalvar;
  };

  const getSidebarItemClass = (index: number) => {
    const classes = ["step-item"];
    if (activeStep === index) classes.push("is-active");
    else if (skippedSteps.has(index)) classes.push("is-skipped");
    else if (isAvulso(index)) classes.push("is-avulso");
    else if (activeStep > index) classes.push("is-completed");
    return classes.join(" ");
  };

  const isAvulsoIncompleto = Object.entries(avulsoConfig).some(
    ([stepIdx, config]) => {
      if (!config.ativo) return false;
      const camposObrigatorios = AVULSO_FIELDS[Number(stepIdx)] || [];
      return camposObrigatorios.some((campo) => !config.ids[campo.key]);
    },
  );

  return (
    <BaseLayout>
      <div className="tecnico-wizard-wrapper">
        <header className="wizard-header">
          <h1>Setup Técnico de Instalação</h1>
          <p>
            Siga o fluxo para cadastrar e vincular os dispositivos na base de
            dados.
          </p>
        </header>

        <div className="wizard-container">
          <aside className="wizard-sidebar">
            <ul className="step-navigation">
              {steps
                .map((step, index) => ({ step, index }))
                .filter(({ step }) => isDev || !step.devOnly)
                .map(({ step, index }) => (
                  <li
                    key={step.id}
                    className={getSidebarItemClass(index)}
                    onClick={() => setActiveStep(index)}
                  >
                    <div className="step-icon-box">{step.icon}</div>

                    <div className="step-info">
                      <span className="step-label">
                        Passo {index + 1}
                        {isAvulso(index) && (
                          <span className="badge-optional badge-avulso">
                            Avulso
                          </span>
                        )}
                        {!isAvulso(index) && step.optional && (
                          <span
                            className={`badge-optional ${
                              skippedSteps.has(index) ? "badge-skipped" : ""
                            }`}
                          >
                            {skippedSteps.has(index) ? "Pulado" : "Opcional"}
                          </span>
                        )}
                      </span>
                      <span className="step-title">{step.title}</span>
                    </div>

                    {skippedSteps.has(index) && (
                      <button
                        className="btn-unskip"
                        title="Reativar este passo"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUnskip(index);
                          setActiveStep(index);
                        }}
                      >
                        <RotateCcw size={13} />
                      </button>
                    )}
                  </li>
                ))}
            </ul>

            {skippedSteps.size > 0 && (
              <div className="skipped-summary">
                <span>
                  {skippedSteps.size} passo{skippedSteps.size > 1 ? "s" : ""}{" "}
                  pulado{skippedSteps.size > 1 ? "s" : ""}
                </span>
                <button
                  className="btn-clear-skipped"
                  onClick={() => setSkippedSteps(new Set())}
                >
                  Reativar todos
                </button>
              </div>
            )}
          </aside>

          <main className="wizard-main-content">
            <div className="form-content-animated">
              {isCurrentStepSkipped && (
                <div className="skipped-banner">
                  <SkipForward size={18} />
                  <span>
                    Este passo está <strong>pulado</strong>. Os dados aqui
                    preenchidos não serão enviados.
                  </span>
                  <button
                    className="action-btn btn-outline-primary btn-sm"
                    onClick={() => handleUnskip(activeStep)}
                  >
                    <RotateCcw size={14} /> Reativar
                  </button>
                </div>
              )}

              {isCurrentStepAvulso && !isCurrentStepSkipped && (
                <AvulsoBanner
                  stepIndex={activeStep}
                  avulsoIds={avulsoConfig[activeStep]?.ids ?? {}}
                  onChange={(key, value) => setAvulsoId(activeStep, key, value)}
                  onDisable={() => disableAvulso(activeStep)}
                />
              )}

              {!isCurrentStepAvulso &&
                !isCurrentStepSkipped &&
                currentStepHasAvulsoFields && (
                  <div className="avulso-trigger">
                    <button
                      className="btn-enable-avulso"
                      onClick={() => enableAvulso(activeStep)}
                    >
                      <Unlink size={15} />
                      Cadastrar de forma avulsa (informar IDs manualmente)
                    </button>
                  </div>
                )}

              {activeStep === 0 && (
                <section className="form-section">
                  <h2>Cadastro da Regional</h2>
                  <div className="form-grid">
                    <div className="input-wrapper field-large">
                      <RequiredLabel required>Nome da Regional</RequiredLabel>
                      <input
                        type="text"
                        placeholder="Ex: Regional Sudeste"
                        value={formData.regional.nome}
                        onChange={(e) =>
                          handleObjectChange("regional", "nome", e.target.value)
                        }
                      />
                    </div>
                    <div className="input-wrapper field-full">
                      <label>Descrição</label>
                      <input
                        type="text"
                        placeholder="Descrição da regional"
                        value={formData.regional.descricao}
                        onChange={(e) =>
                          handleObjectChange(
                            "regional",
                            "descricao",
                            e.target.value,
                          )
                        }
                      />
                    </div>
                    <div className="input-wrapper">
                      <h4
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                      >
                        Status
                      </h4>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.regional.ativo === 1}
                          onChange={(e) =>
                            handleObjectChange(
                              "regional",
                              "ativo",
                              e.target.checked ? 1 : 0,
                            )
                          }
                        />
                        Ativo
                      </label>
                    </div>
                  </div>
                </section>
              )}

              {activeStep === 1 && (
                <section className="form-section">
                  <h2>Cadastro da Loja</h2>
                  <div className="form-grid">
                    <div className="input-wrapper">
                      <label>Nº da Loja</label>
                      <input
                        type="text"
                        placeholder="Ex: 112"
                        value={formData.loja.numero}
                        onChange={(e) =>
                          handleObjectChange("loja", "numero", e.target.value)
                        }
                      />
                    </div>
                    <div className="input-wrapper field-large">
                      <label>Nome da Loja</label>
                      <input
                        type="text"
                        placeholder="Ex: XPS Log T"
                        value={formData.loja.nome}
                        onChange={(e) =>
                          handleObjectChange("loja", "nome", e.target.value)
                        }
                      />
                    </div>
                    <div className="input-wrapper field-full">
                      <label>Endereço Completo</label>
                      <input
                        type="text"
                        placeholder="Rua, Bairro, Cidade..."
                        value={formData.loja.endereco}
                        onChange={(e) =>
                          handleObjectChange("loja", "endereco", e.target.value)
                        }
                      />
                    </div>
                    <div className="input-wrapper field-full">
                      <label>Arquivo Planta</label>
                      <input
                        type="text"
                        placeholder={formData.loja.arquivoPlanta}
                        onChange={(e) =>
                          handleObjectChange(
                            "loja",
                            "arquivoPlanta",
                            e.target.value,
                          )
                        }
                      />
                    </div>
                    <div className="input-wrapper">
                      <label>UF</label>
                      <input
                        type="text"
                        placeholder="Ex: RJ"
                        maxLength={2}
                        value={formData.loja.uf}
                        onChange={(e) =>
                          handleObjectChange("loja", "uf", e.target.value)
                        }
                      />
                    </div>
                    <div className="input-wrapper">
                      <RequiredLabel required>Cliente</RequiredLabel>
                      {skippedSteps.has(0) ? (
                        <select
                          title="Selecione a Regional"
                          value={formData.loja.idRegional}
                          onChange={(e) =>
                            handleObjectChange(
                              "loja",
                              "idRegional",
                              handleNumberInput(e.target.value),
                            )
                          }
                        >
                          <option value={0}>
                            Selecione uma regional existente...
                          </option>

                          {todasRegionais.map((reg: any) => (
                            <option key={reg.id} value={reg.id}>
                              {reg.nome}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          disabled
                          placeholder="Preenchido automaticamente pelo Passo 1"
                          value={
                            formData.regional.nome ||
                            clienteObjetoSelected?.nome ||
                            ""
                          }
                        />
                      )}
                    </div>
                    <div className="input-wrapper">
                      <label>Importância Segurada</label>
                      <input
                        type="text"
                        placeholder="R$ 0,00"
                        value={formatCurrency(
                          formData.loja.importanciaSegurada,
                        )}
                        onChange={(e) => {
                          const rawValue = e.target.value.replace(/\D/g, "");
                          handleObjectChange(
                            "loja",
                            "importanciaSegurada",
                            rawValue,
                          );
                        }}
                      />
                    </div>
                    <div className="input-wrapper">
                      <label>Latitude</label>
                      <input
                        type="text"
                        placeholder="-22.8884"
                        value={formData.loja.latitude}
                        onChange={(e) =>
                          handleObjectChange("loja", "latitude", e.target.value)
                        }
                      />
                    </div>
                    <div className="input-wrapper">
                      <label>Longitude</label>
                      <input
                        type="text"
                        placeholder="-43.1147"
                        value={formData.loja.longitude}
                        onChange={(e) =>
                          handleObjectChange(
                            "loja",
                            "longitude",
                            e.target.value,
                          )
                        }
                      />
                    </div>
                    <div className="input-wrapper">
                      <label>CNPJ</label>
                      <input
                        type="text"
                        placeholder="00.000.000/0000-00"
                        value={formData.loja.cnpj}
                        onChange={(e) =>
                          handleObjectChange("loja", "cnpj", e.target.value)
                        }
                      />
                    </div>
                    <div className="input-wrapper">
                      <label>Vigência</label>
                      <input
                        title={""}
                        type="date"
                        value={formData.loja.vigencia}
                        onChange={(e) =>
                          handleObjectChange("loja", "vigencia", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </section>
              )}

              {activeStep === 2 && (
                <section className="form-section">
                  <div className="section-header-flex">
                    <h2>Dispositivos Centrais</h2>
                    <button
                      className="action-btn btn-outline-primary" // Adicionado action-btn
                      onClick={() => addMonitor("dispcentral", defaultCentral)}
                    >
                      <Plus size={16} /> Adicionar Central
                    </button>
                  </div>

                  {formData.dispcentral.map((central, index) => (
                    <div key={index} className="monitor-card">
                      <div className="monitor-card-header">
                        <h3>Central #{index + 1}</h3>
                        {formData.dispcentral.length > 1 && (
                          <button
                            className="btn-remove"
                            onClick={() => removeMonitor("dispcentral", index)}
                          >
                            <Trash2 size={16} /> Remover
                          </button>
                        )}
                      </div>

                      <h3 className="section-subtitle">
                        Informações do Equipamento
                      </h3>
                      <div className="form-grid">
                        <div className="input-wrapper">
                          <label>Modelo</label>
                          <select
                            title={""}
                            value={central.modelo}
                            onChange={(e) => {
                              const newModelo = e.target.value;
                              handleArrayChange(
                                "dispcentral",
                                index,
                                "modelo",
                                newModelo,
                              );
                              formData.dispmodulo.forEach((m, mIdx) => {
                                if (m.centralIndex === index) {
                                  handleArrayChange(
                                    "dispmodulo",
                                    mIdx,
                                    "modelo",
                                    getModeloModuloFromCentral(newModelo),
                                  );
                                }
                              });
                            }}
                          >
                            <option value="CA8D4">CA8D4</option>
                            <option value="CA8D8">CA8D8</option>
                          </select>
                        </div>
                        <div className="input-wrapper">
                          <label>Versão</label>
                          <input
                            title={""}
                            type="text"
                            value={central.versao}
                            onChange={(e) =>
                              handleArrayChange(
                                "dispcentral",
                                index,
                                "versao",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                        <div className="input-wrapper">
                          <label>Patrimônio</label>
                          <input
                            title={""}
                            type="text"
                            placeholder="Ex: 00123"
                            value={central.patrimonio}
                            onChange={(e) =>
                              handleArrayChange(
                                "dispcentral",
                                index,
                                "patrimonio",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                        <div className="input-wrapper">
                          <label>Senha AP</label>
                          <input
                            title={""}
                            type="text"
                            value={central.apPassword}
                            onChange={(e) =>
                              handleArrayChange(
                                "dispcentral",
                                index,
                                "apPassword",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>

                      <h3 className="section-subtitle">
                        Configurações de Rede e Lora
                      </h3>
                      <div className="form-grid">
                        <div className="input-wrapper field-large">
                          <label>End Point da API</label>
                          <input
                            title={""}
                            type="url"
                            value={central.endPoint}
                            onChange={(e) =>
                              handleArrayChange(
                                "dispcentral",
                                index,
                                "endPoint",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                        <div className="input-wrapper">
                          <label>Taxa Envio (s)</label>
                          <input
                            title={""}
                            type="number"
                            value={central.taxaEnvio}
                            onChange={(e) =>
                              handleArrayChange(
                                "dispcentral",
                                index,
                                "taxaEnvio",
                                handleNumberInput(e.target.value),
                              )
                            }
                          />
                        </div>
                        <div className="input-wrapper">
                          <label>Banda Lora (Hz)</label>
                          <input
                            title={""}
                            type="number"
                            value={central.loraBand}
                            onChange={(e) =>
                              handleArrayChange(
                                "dispcentral",
                                index,
                                "loraBand",
                                handleNumberInput(e.target.value),
                              )
                            }
                          />
                        </div>
                        <div className="input-wrapper">
                          <label>Limite Erro API</label>
                          <input
                            title={""}
                            type="number"
                            value={central.apiErrorLimit}
                            onChange={(e) =>
                              handleArrayChange(
                                "dispcentral",
                                index,
                                "apiErrorLimit",
                                handleNumberInput(e.target.value),
                              )
                            }
                          />
                        </div>
                        <div className="input-wrapper">
                          <label>Resolução Analógica</label>
                          <input
                            title={""}
                            type="number"
                            value={central.analogResolution}
                            onChange={(e) =>
                              handleArrayChange(
                                "dispcentral",
                                index,
                                "analogResolution",
                                handleNumberInput(e.target.value),
                              )
                            }
                          />
                        </div>
                      </div>

                      <h3 className="section-subtitle">Notificações</h3>
                      <div
                        className="checkbox-group"
                        style={{
                          display: "flex",
                          gap: "20px",
                          marginTop: "10px",
                        }}
                      >
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={central.alertaAtivo === 1}
                            onChange={(e) =>
                              handleArrayChange(
                                "dispcentral",
                                index,
                                "alertaAtivo",
                                e.target.checked ? 1 : 0,
                              )
                            }
                          />{" "}
                          Alerta Ativo
                        </label>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={central.envioEmailAtivo === 1}
                            onChange={(e) =>
                              handleArrayChange(
                                "dispcentral",
                                index,
                                "envioEmailAtivo",
                                e.target.checked ? 1 : 0,
                              )
                            }
                          />{" "}
                          E-mail
                        </label>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={central.envioWhatsAtivo === 1}
                            onChange={(e) =>
                              handleArrayChange(
                                "dispcentral",
                                index,
                                "envioWhatsAtivo",
                                e.target.checked ? 1 : 0,
                              )
                            }
                          />{" "}
                          WhatsApp
                        </label>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={central.envioTelegramAtivo === 1}
                            onChange={(e) =>
                              handleArrayChange(
                                "dispcentral",
                                index,
                                "envioTelegramAtivo",
                                e.target.checked ? 1 : 0,
                              )
                            }
                          />{" "}
                          Telegram
                        </label>
                      </div>
                    </div>
                  ))}
                </section>
              )}

              {activeStep === 3 && (
                <section className="form-section">
                  <div className="section-header-flex">
                    <h2>Módulos Remotos</h2>
                    <button
                      className="action-btn btn-outline-primary"
                      onClick={() =>
                        addMonitor("dispmodulo", {
                          adrModulo: 0,
                          modelo: getModeloModuloFromCentral(
                            formData.dispcentral[0]?.modelo ?? "CA8D4",
                          ),
                          versao: "4.0",
                          toleranciaOffline: 1,
                          centralIndex: 0,
                        })
                      }
                    >
                      <Plus size={16} /> Adicionar Módulo
                    </button>
                  </div>

                  {formData.dispmodulo.map((modulo, index) => (
                    <div key={index} className="monitor-card">
                      <div className="monitor-card-header">
                        <h3>Módulo #{index + 1}</h3>
                        {formData.dispmodulo.length > 1 && (
                          <button
                            className="btn-remove"
                            onClick={() => removeMonitor("dispmodulo", index)}
                          >
                            <Trash2 size={16} /> Remover
                          </button>
                        )}
                      </div>
                      <div className="form-grid">
                        {/* Vínculo à central: oculto no modo avulso pois o idCentral
                            já é informado manualmente no banner */}
                        {!isCurrentStepAvulso && (
                          <div className="input-wrapper field-large">
                            <label>Vincular à Central</label>
                            <select
                              title={""}
                              value={modulo.centralIndex}
                              onChange={(e) => {
                                const centralIdx = parseInt(e.target.value);
                                const central =
                                  formData.dispcentral[centralIdx];
                                handleArrayChange(
                                  "dispmodulo",
                                  index,
                                  "centralIndex",
                                  centralIdx,
                                );
                                if (central) {
                                  handleArrayChange(
                                    "dispmodulo",
                                    index,
                                    "modelo",
                                    getModeloModuloFromCentral(central.modelo),
                                  );
                                }
                              }}
                            >
                              {formData.dispcentral.map((c, i) => (
                                <option key={i} value={i}>
                                  Central {i + 1} ({c.modelo})
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div className="input-wrapper">
                          <RequiredLabel required>
                            Endereço do Módulo (adrModulo)
                          </RequiredLabel>
                          <input
                            title={""}
                            type="number"
                            value={modulo.adrModulo}
                            onChange={(e) =>
                              handleArrayChange(
                                "dispmodulo",
                                index,
                                "adrModulo",
                                handleNumberInput(e.target.value),
                              )
                            }
                          />
                        </div>
                        <div className="input-wrapper">
                          <label>Modelo</label>
                          {isCurrentStepAvulso ? (
                            <select
                              title={""}
                              value={modulo.modelo}
                              onChange={(e) =>
                                handleArrayChange(
                                  "dispmodulo",
                                  index,
                                  "modelo",
                                  e.target.value,
                                )
                              }
                            >
                              <option value="A8D4">A8D4</option>
                              <option value="A8D8">A8D8</option>
                            </select>
                          ) : (
                            <input
                              type="text"
                              disabled
                              title="Sincronizado automaticamente com a Central vinculada"
                              value={modulo.modelo}
                            />
                          )}
                        </div>
                        <div className="input-wrapper">
                          <label>Versão</label>
                          <input
                            title={""}
                            type="text"
                            value={modulo.versao}
                            onChange={(e) =>
                              handleArrayChange(
                                "dispmodulo",
                                index,
                                "versao",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                        <div className="input-wrapper">
                          <label>Tolerância Offline (min)</label>
                          <input
                            title={""}
                            type="number"
                            value={modulo.toleranciaOffline}
                            onChange={(e) =>
                              handleArrayChange(
                                "dispmodulo",
                                index,
                                "toleranciaOffline",
                                handleNumberInput(e.target.value),
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </section>
              )}

              {activeStep === 4 && (isDev || !steps[4].devOnly) && (
                <section className="form-section">
                  <div className="section-header-flex">
                    <h2>Monitores Analógicos</h2>
                    <button
                      className="action-btn btn-outline-primary"
                      onClick={() =>
                        addMonitor("monanalogico", defaultAnalogico)
                      }
                    >
                      <Plus size={16} /> Adicionar Monitor
                    </button>
                  </div>

                  {formData.monanalogico.map((monitor, index) => (
                    <div key={index} className="monitor-card">
                      <div className="monitor-card-header">
                        <h3>Monitor #{index + 1}</h3>
                        {formData.monanalogico.length > 1 && (
                          <button
                            className="btn-remove"
                            onClick={() => removeMonitor("monanalogico", index)}
                          >
                            <Trash2 size={16} /> Remover
                          </button>
                        )}
                      </div>
                      <div className="form-grid">
                        <div className="input-wrapper">
                          <RequiredLabel required>
                            ID Entrada (Porta)
                          </RequiredLabel>
                          <input
                            title={""}
                            type="number"
                            value={monitor.idEntrada}
                            min="1"
                            onChange={(e) =>
                              handleArrayChange(
                                "monanalogico",
                                index,
                                "idEntrada",
                                handleNumberInput(e.target.value),
                              )
                            }
                          />
                        </div>

                        {!isCurrentStepAvulso && (
                          <div className="input-wrapper">
                            <label>Vincular ao Módulo</label>
                            <select
                              title="Selecione o módulo"
                              value={(monitor as any).moduloIndex}
                              onChange={(e) => {
                                const moduloIdx = parseInt(e.target.value);
                                handleArrayChange(
                                  "monanalogico",
                                  index,
                                  "moduloIndex",
                                  moduloIdx,
                                );
                                handleArrayChange(
                                  "monanalogico",
                                  index,
                                  "modeloModulo",
                                  formData.dispmodulo[moduloIdx].modelo,
                                );
                              }}
                            >
                              {formData.dispmodulo.map((m, i) => (
                                <option key={i} value={i}>
                                  Módulo {i + 1} (Adr: {m.adrModulo}) - Central{" "}
                                  {m.centralIndex + 1}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        <div className="input-wrapper">
                          <label>Grupo</label>
                          <select
                            title={""}
                            value={monitor.idGrupoMonitor}
                            onChange={(e) =>
                              handleArrayChange(
                                "monanalogico",
                                index,
                                "idGrupoMonitor",
                                parseInt(e.target.value),
                              )
                            }
                          >
                            <option value={1}>Incêndio</option>
                            <option value={2}>Ativos Críticos</option>
                          </select>
                        </div>
                        <div className="input-wrapper field-large">
                          <label>Equipamento Monitorado</label>
                          <select
                            title={""}
                            value={monitor.idEquipamento}
                            onChange={(e) =>
                              handleArrayChange(
                                "monanalogico",
                                index,
                                "idEquipamento",
                                parseInt(e.target.value),
                              )
                            }
                          >
                            {eqpAnalogicos.map((eqp) => (
                              <option key={eqp.id} value={eqp.id}>
                                {eqp.nome}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="input-wrapper">
                          <label>Unidade de Medida</label>
                          <input
                            title={""}
                            type="text"
                            value={monitor.unidadeMedida}
                            onChange={(e) =>
                              handleArrayChange(
                                "monanalogico",
                                index,
                                "unidadeMedida",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                        <div className="input-wrapper">
                          <label>Limite Inf / Sup</label>
                          <div style={{ display: "flex", gap: "10px" }}>
                            <input
                              title={""}
                              type="number"
                              value={monitor.limiteInf}
                              style={{ width: "50%" }}
                              onChange={(e) =>
                                handleArrayChange(
                                  "monanalogico",
                                  index,
                                  "limiteInf",
                                  handleNumberInput(e.target.value),
                                )
                              }
                            />
                            <input
                              title={""}
                              type="number"
                              value={monitor.limiteSup}
                              style={{ width: "50%" }}
                              onChange={(e) =>
                                handleArrayChange(
                                  "monanalogico",
                                  index,
                                  "limiteSup",
                                  handleNumberInput(e.target.value),
                                )
                              }
                            />
                          </div>
                        </div>
                        <div className="input-wrapper">
                          <label>Escala Inf / Sup</label>
                          <div style={{ display: "flex", gap: "10px" }}>
                            <input
                              title={""}
                              type="number"
                              value={monitor.escalaInf}
                              style={{ width: "50%" }}
                              onChange={(e) =>
                                handleArrayChange(
                                  "monanalogico",
                                  index,
                                  "escalaInf",
                                  handleNumberInput(e.target.value),
                                )
                              }
                            />
                            <input
                              title={""}
                              type="number"
                              value={monitor.escalaSup}
                              style={{ width: "50%" }}
                              onChange={(e) =>
                                handleArrayChange(
                                  "monanalogico",
                                  index,
                                  "escalaSup",
                                  handleNumberInput(e.target.value),
                                )
                              }
                            />
                          </div>
                        </div>
                        <div className="input-wrapper">
                          <label>Calibração Escala</label>
                          <input
                            title={""}
                            type="number"
                            value={monitor.calibracaoEscala}
                            onChange={(e) =>
                              handleArrayChange(
                                "monanalogico",
                                index,
                                "calibracaoEscala",
                                handleNumberInput(e.target.value),
                              )
                            }
                          />
                        </div>
                        <div className="input-wrapper field-full">
                          <label>Comentários (Local)</label>
                          <input
                            title={""}
                            type="text"
                            value={monitor.comentarios}
                            onChange={(e) =>
                              handleArrayChange(
                                "monanalogico",
                                index,
                                "comentarios",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </section>
              )}

              {activeStep === 5 && (isDev || !steps[5].devOnly) && (
                <section className="form-section">
                  <div className="section-header-flex">
                    <h2>Monitores Digitais</h2>
                    <button
                      className="action-btn btn-outline-primary"
                      onClick={() => addMonitor("mondigital", defaultDigital)}
                    >
                      <Plus size={16} /> Adicionar Monitor
                    </button>
                  </div>

                  {formData.mondigital.map((monitor, index) => (
                    <div key={index} className="monitor-card">
                      <div className="monitor-card-header">
                        <h3>Monitor #{index + 1}</h3>
                        {formData.mondigital.length > 1 && (
                          <button
                            className="btn-remove"
                            onClick={() => removeMonitor("mondigital", index)}
                          >
                            <Trash2 size={16} /> Remover
                          </button>
                        )}
                      </div>
                      <div className="form-grid">
                        <div className="input-wrapper">
                          <label>ID Entrada (Porta)</label>
                          <input
                            title={""}
                            type="number"
                            value={monitor.idEntrada}
                            min="1"
                            onChange={(e) =>
                              handleArrayChange(
                                "mondigital",
                                index,
                                "idEntrada",
                                handleNumberInput(e.target.value),
                              )
                            }
                          />
                        </div>

                        {!isCurrentStepAvulso && (
                          <div className="input-wrapper">
                            <label>Vincular ao Módulo</label>
                            <select
                              title="Selecione o módulo"
                              value={(monitor as any).moduloIndex}
                              onChange={(e) => {
                                const moduloIdx = parseInt(e.target.value);
                                handleArrayChange(
                                  "mondigital",
                                  index,
                                  "moduloIndex",
                                  moduloIdx,
                                );
                                handleArrayChange(
                                  "mondigital",
                                  index,
                                  "modeloModulo",
                                  formData.dispmodulo[moduloIdx].modelo,
                                );
                              }}
                            >
                              {formData.dispmodulo.map((m, i) => (
                                <option key={i} value={i}>
                                  Módulo {i + 1} (Adr: {m.adrModulo}) - Central{" "}
                                  {m.centralIndex + 1}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        <div className="input-wrapper">
                          <label>Grupo</label>
                          <select
                            title={""}
                            value={monitor.idGrupoMonitor}
                            onChange={(e) =>
                              handleArrayChange(
                                "mondigital",
                                index,
                                "idGrupoMonitor",
                                parseInt(e.target.value),
                              )
                            }
                          >
                            <option value={1}>Incêndio</option>
                            <option value={2}>Ativos Críticos</option>
                          </select>
                        </div>
                        <div className="input-wrapper field-large">
                          <label>Equipamento Monitorado</label>
                          <select
                            title={""}
                            value={monitor.idEquipamento}
                            onChange={(e) =>
                              handleArrayChange(
                                "mondigital",
                                index,
                                "idEquipamento",
                                parseInt(e.target.value),
                              )
                            }
                          >
                            {eqpDigitais.map((eqp) => (
                              <option key={eqp.id} value={eqp.id}>
                                {eqp.nome}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="input-wrapper">
                          <label>Valor Padrão Normal</label>
                          <select
                            title={""}
                            value={monitor.valorPadraoNormal}
                            onChange={(e) =>
                              handleArrayChange(
                                "mondigital",
                                index,
                                "valorPadraoNormal",
                                parseInt(e.target.value),
                              )
                            }
                          >
                            <option value={1}>1 (Fechado / Ligado)</option>
                            <option value={0}>0 (Aberto / Desligado)</option>
                          </select>
                        </div>
                        <div className="input-wrapper field-large">
                          <label>Comentários</label>
                          <input
                            title={""}
                            type="text"
                            value={monitor.comentarios}
                            onChange={(e) =>
                              handleArrayChange(
                                "mondigital",
                                index,
                                "comentarios",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </section>
              )}

              {activeStep === 6 && (isDev || !steps[6].devOnly) && (
                <section className="form-section">
                  <div className="section-header-flex">
                    <h2>Monitores de Medição</h2>
                    <button
                      className="action-btn btn-outline-primary"
                      onClick={() => addMonitor("monmedidor", defaultMedidor)}
                    >
                      <Plus size={16} /> Adicionar Medidor
                    </button>
                  </div>

                  {formData.monmedidor.length === 0 && (
                    <div
                      style={{
                        padding: "20px",
                        textAlign: "center",
                        color: "#94a3b8",
                        border: "1px dashed #e2e8f0",
                        borderRadius: "8px",
                      }}
                    >
                      Nenhum medidor cadastrado. Clique no botão acima para
                      adicionar.
                    </div>
                  )}

                  {formData.monmedidor.map((monitor, index) => (
                    <div key={index} className="monitor-card">
                      <div className="monitor-card-header">
                        <h3>Medidor #{index + 1}</h3>
                        {formData.monmedidor.length > 1 && (
                          <button
                            className="btn-remove"
                            onClick={() => removeMonitor("monmedidor", index)}
                          >
                            <Trash2 size={16} /> Remover
                          </button>
                        )}
                      </div>
                      <div className="form-grid">
                        <div className="input-wrapper">
                          <label>Dia de Medição</label>
                          <input
                            title={""}
                            type="number"
                            value={monitor.diaMedicao}
                            onChange={(e) =>
                              handleArrayChange(
                                "monmedidor",
                                index,
                                "diaMedicao",
                                handleNumberInput(e.target.value),
                              )
                            }
                          />
                        </div>
                        <div className="input-wrapper">
                          <label>ID Entrada (Porta)</label>
                          <input
                            title={""}
                            type="number"
                            value={monitor.idEntrada}
                            min="1"
                            onChange={(e) =>
                              handleArrayChange(
                                "monmedidor",
                                index,
                                "idEntrada",
                                handleNumberInput(e.target.value),
                              )
                            }
                          />
                        </div>
                        {!isCurrentStepAvulso && (
                          <div className="input-wrapper">
                            <label>Vincular ao Módulo</label>
                            <select
                              title="Selecione o módulo"
                              value={(monitor as any).moduloIndex ?? 0}
                              onChange={(e) => {
                                const moduloIdx = parseInt(e.target.value);
                                handleArrayChange(
                                  "monmedidor",
                                  index,
                                  "moduloIndex",
                                  moduloIdx,
                                );
                                handleArrayChange(
                                  "monmedidor",
                                  index,
                                  "modeloModulo",
                                  formData.dispmodulo[moduloIdx].modelo,
                                );
                              }}
                            >
                              {formData.dispmodulo.map((m, i) => (
                                <option key={i} value={i}>
                                  Módulo {i + 1} (Adr: {m.adrModulo}) - Central{" "}
                                  {m.centralIndex + 1}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div className="input-wrapper">
                          <label>Grupo</label>
                          <select
                            title={""}
                            value={monitor.idGrupoMonitor}
                            onChange={(e) =>
                              handleArrayChange(
                                "monmedidor",
                                index,
                                "idGrupoMonitor",
                                parseInt(e.target.value),
                              )
                            }
                          >
                            <option value={1}>Incêndio</option>
                            <option value={2}>Ativos Críticos</option>
                          </select>
                        </div>
                        <div className="input-wrapper field-large">
                          <label>Equipamento Monitorado</label>
                          <select
                            title={""}
                            value={monitor.idEquipamento}
                            onChange={(e) =>
                              handleArrayChange(
                                "monmedidor",
                                index,
                                "idEquipamento",
                                parseInt(e.target.value),
                              )
                            }
                          >
                            {eqpMedidores.map((eqp) => (
                              <option key={eqp.id} value={eqp.id}>
                                {eqp.nome}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="input-wrapper">
                          <label>Unidade de Medida</label>
                          <input
                            title={""}
                            type="text"
                            value={monitor.unidadeMedida}
                            onChange={(e) =>
                              handleArrayChange(
                                "monmedidor",
                                index,
                                "unidadeMedida",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </section>
              )}
            </div>

            <footer
              className="wizard-footer"
              style={{
                marginTop: 30,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <button
                className="action-btn btn-secondary"
                onClick={handlePrev}
                disabled={activeStep === 0 || loading}
              >
                <ChevronLeft size={16} /> Voltar
              </button>

              <div
                style={{ display: "flex", gap: "10px", alignItems: "center" }}
              >
                {isCurrentStepOptional &&
                  !isCurrentStepSkipped &&
                  !isCurrentStepAvulso && (
                    <button
                      className="action-btn btn-skip"
                      onClick={handleSkip}
                      disabled={loading}
                    >
                      <SkipForward size={16} /> Pular
                    </button>
                  )}

                {isCurrentStepSkipped && (
                  <button
                    className="action-btn btn-outline-primary btn-sm"
                    onClick={() => handleUnskip(activeStep)}
                    disabled={loading}
                  >
                    <RotateCcw size={14} /> Reativar passo
                  </button>
                )}

                {activeStep < lastStepIndex ? (
                  <button
                    className="action-btn btn-primary"
                    onClick={handleNext}
                    disabled={loading || !canGoNext()}
                  >
                    Próximo <ChevronRight size={16} />
                  </button>
                ) : (
                  <button
                    className="action-btn btn-success"
                    onClick={onSubmit}
                    disabled={loading || isAvulsoIncompleto || isFormVazio()}
                  >
                    <Save size={16} />
                    {isFormVazio()
                      ? "Adicione dados para salvar"
                      : "Salvar Final"}
                  </button>
                )}
              </div>
            </footer>
          </main>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <small style={{ color: "#aaa7a7" }}>Versão: {versaoFormatada}</small>
      </div>
    </BaseLayout>
  );
};

export default TecnicoProcessoCriacoesPage;
